/**
 * Block F.5: детальная карточка клиента для owner.
 * Показывает: инфо, баланс+операции, подписки (freeze/unfreeze + устройства),
 * платежи, рефералы, wallet ledger, UTM.
 * Действия: топап/списание баланса, бан/разбан, заморозка подписок.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiErrorCode } from '@/lib/api';
import type { ClientSubscriptionBrief, OwnerSubscriptionTraffic } from '@/api/types';
import { Card, CardContent } from '@/components/ui/card';
import { BottomSheet, ConfirmDialog } from '@/components/ui/dialog';
import {
  User as UserIcon,
  Wallet,
  Loader2,
  Plus,
  Minus,
  CheckCircle2,
  XCircle,
  Snowflake,
  Sun,
  Smartphone,
  CreditCard,
  Gift,
  Ban,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  Stethoscope,
  MessageSquare,
  ArrowLeftRight,
} from 'lucide-react';
import { haptic } from '@/lib/telegram';
import { useTgBack } from '@/lib/use-back';
import { CopyValue, shortId } from '@/components/CopyValue';
import { TONE_CLS, money, paymentStatus, providerName } from '@/lib/labels';
import { toast } from 'sonner';
import { MessageSheet } from '@/components/MessageSheet';

export const Route = createFileRoute('/clients/$id')({ component: ClientDetail });

function ClientDetail() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const [topupOpen, setTopupOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [anonOpen, setAnonOpen] = useState(false);

  const { data: c, isPending } = useQuery({
    queryKey: ['client', id],
    queryFn: () => api.clientDetail(id),
  });

  // Назад туда, откуда пришли: в поиск с тем же запросом или в карточку подписки.
  useTgBack();

  const invalidate = () => qc.invalidateQueries({ queryKey: ['client', id] });

  const sendMsg = useMutation({
    mutationFn: (text: string) => api.messageClient(id, text),
    onSuccess: () => {
      haptic('success');
      toast.success('Сообщение отправлено');
      setMsgOpen(false);
    },
    onError: () => {
      haptic('error');
      toast.error('Не доставлено: клиент остановил или заблокировал бота.');
    },
  });

  const anonymize = useMutation({
    mutationFn: () => api.anonymizeClient(id),
    onSuccess: () => {
      haptic('success');
      toast.success('Клиент анонимизирован (GDPR)');
      void qc.invalidateQueries({ queryKey: ['client', id] });
    },
    onError: () => toast.error('Не удалось анонимизировать'),
  });

  const { mutate: toggleBan, isPending: banning } = useMutation({
    mutationFn: (ban: boolean) => (ban ? api.banClient(id) : api.unbanClient(id)),
    onSuccess: (_res, ban) => {
      invalidate();
      haptic(ban ? 'warning' : 'success');
      toast.success(ban ? 'Клиент забанен' : 'Клиент разбанен');
    },
    onError: () => {
      haptic('error');
      toast.error('Не удалось');
    },
  });

  if (isPending || !c) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-32">
      <div className="px-2 pt-2 flex items-start gap-3">
        <div className="w-14 h-14 rounded-2xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
          <UserIcon className="w-7 h-7" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-bold tracking-tight truncate">
            {c.tg_username
              ? `@${c.tg_username}`
              : c.tg_user_id != null
                ? `tg_id=${c.tg_user_id}`
                : (c.email ?? 'без Telegram')}
          </h2>
          <p className="text-xs text-muted-foreground">
            {c.partner_display_name || '—'} · {c.locale}
            {c.origin === 'web' ? ' · с сайта, вход по почте' : c.origin === 'api' ? ' · через API' : ''}
          </p>
        </div>
        {c.is_blocked ? (
          <XCircle className="w-5 h-5 text-red-500" aria-label="заблокировал бота" />
        ) : c.is_active ? (
          <CheckCircle2 className="w-5 h-5 text-green-500" />
        ) : (
          <XCircle className="w-5 h-5 text-yellow-500" aria-label="забанен админом" />
        )}
      </div>

      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold">Баланс</span>
            </div>
            <span className="text-xl font-bold">
              ${(c.balance_usd_minor / 100).toFixed(2)}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setTopupOpen(true)}
            className="w-full bg-primary/10 text-primary py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
          >
            <Wallet className="w-4 h-4" />
            Изменить баланс
          </button>
          <button
            type="button"
            onClick={() => setMsgOpen(true)}
            className="w-full bg-white/5 text-foreground py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
          >
            <MessageSquare className="w-4 h-4" />
            Написать клиенту
          </button>
          <MessageSheet
            open={msgOpen}
            onClose={() => setMsgOpen(false)}
            title="Сообщение клиенту"
            onSend={(text) => sendMsg.mutate(text)}
            pending={sendMsg.isPending}
          />
          <button
            type="button"
            disabled={banning}
            onClick={() => toggleBan(c.is_active)}
            className={`w-full py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 ${
              c.is_active
                ? 'bg-red-500/10 text-red-500'
                : 'bg-green-500/10 text-green-500'
            }`}
          >
            {banning ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : c.is_active ? (
              <Ban className="w-4 h-4" />
            ) : (
              <ShieldCheck className="w-4 h-4" />
            )}
            {c.is_active ? 'Забанить клиента' : 'Разбанить клиента'}
          </button>
          <button
            type="button"
            onClick={() => setAnonOpen(true)}
            className="w-full py-2.5 rounded-xl text-xs font-bold text-muted-foreground/70 hover:text-red-400"
          >
            Анонимизировать (GDPR)
          </button>
          <ConfirmDialog
            open={anonOpen}
            onClose={() => setAnonOpen(false)}
            onConfirm={() => anonymize.mutate()}
            title="Анонимизировать клиента?"
            description="PII клиента (username, имя) будет стёрто безвозвратно. Право на забвение. Действие необратимо."
            confirmLabel="Анонимизировать"
            destructive
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Подписки ({c.subscriptions.length})
          </h3>
          {c.subscriptions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Нет подписок</p>
          ) : (
            c.subscriptions
              .slice(0, 15)
              .map((s) => <SubscriptionRow key={s.id} sub={s} onChanged={invalidate} />)
          )}
        </CardContent>
      </Card>

      {c.payments.length > 0 && (
        <Card>
          <CardContent className="p-5 space-y-1">
            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
              <CreditCard className="w-3.5 h-3.5" />
              Платежи ({c.payments.length})
            </h3>
            {c.payments.slice(0, 15).map((p) => {
              const st = paymentStatus(p.status);
              return (
                <Link
                  key={p.id}
                  to="/payments/$id"
                  params={{ id: p.id }}
                  className="flex items-center justify-between gap-2 text-xs py-1.5 border-b border-white/5 last:border-0 active:bg-white/5"
                >
                  <div className="min-w-0">
                    <div className="font-semibold truncate">
                      {money(p.gross_amount_minor ?? p.amount_minor, p.currency)} · {providerName(p.provider)}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {new Date(p.created_at).toLocaleString('ru')}
                    </div>
                    {p.provider_invoice_id && (
                      <CopyValue
                        value={p.provider_invoice_id}
                        label={shortId(p.provider_invoice_id)}
                        title="номер у кассы"
                        className="text-muted-foreground text-[10px]"
                      />
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${TONE_CLS[st.tone]}`}>
                      {st.label}
                    </span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </Link>
              );
            })}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-5 space-y-2">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1.5">
            <Gift className="w-3.5 h-3.5" />
            Рефералы
          </h3>
          <Row
            label="Привёл"
            value={
              c.referrer_id
                ? c.referrer_tg_username
                  ? `@${c.referrer_tg_username}`
                  : c.referrer_id.slice(0, 8)
                : '—'
            }
          />
          <Row label="Пригласил" value={`${c.referred_count} чел.`} />
        </CardContent>
      </Card>

      {(c.utm_source || c.utm_campaign || c.utm_medium) && (
        <Card>
          <CardContent className="p-5 space-y-2">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              UTM-атрибуция
            </div>
            {c.utm_source && <Row label="source" value={c.utm_source} />}
            {c.utm_campaign && <Row label="campaign" value={c.utm_campaign} />}
            {c.utm_medium && <Row label="medium" value={c.utm_medium} />}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-5 space-y-1">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
            Ledger (последние {c.ledger.length})
          </h3>
          {c.ledger.length === 0 ? (
            <p className="text-sm text-muted-foreground">Нет проводок</p>
          ) : (
            c.ledger.slice(0, 20).map((e) => (
              <div
                key={e.tx_id + e.created_at}
                className="flex items-center justify-between text-xs py-1 border-b border-white/5 last:border-0"
              >
                <div className="min-w-0">
                  <div className="font-semibold">{e.reason}</div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {e.description || new Date(e.created_at).toLocaleString('ru')}
                  </div>
                </div>
                <div
                  className={`font-mono whitespace-nowrap ml-2 ${
                    e.amount_minor >= 0 ? 'text-green-500' : 'text-red-500'
                  }`}
                >
                  {e.amount_minor >= 0 ? '+' : ''}
                  {(e.amount_minor / 100).toFixed(2)} {e.currency}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <TopupSheet
        open={topupOpen}
        onClose={() => setTopupOpen(false)}
        clientId={c.id}
        onSuccess={invalidate}
      />
    </div>
  );
}

const toGb = (bytes: number) => bytes / 1024 / 1024 / 1024;

function formatTraffic(traffic?: OwnerSubscriptionTraffic): string {
  if (!traffic || traffic.used_traffic_bytes === null) {
    return traffic?.traffic_limit_bytes != null
      ? `— / ${Math.floor(toGb(traffic.traffic_limit_bytes))} ГБ`
      : '—';
  }
  const used = toGb(traffic.used_traffic_bytes).toFixed(2);
  if (traffic.traffic_limit_bytes === null) return `${used} ГБ · безлимит`;
  const left = Math.max(0, toGb(traffic.traffic_limit_bytes - traffic.used_traffic_bytes));
  return `${used} / ${Math.floor(toGb(traffic.traffic_limit_bytes))} ГБ · осталось ${left.toFixed(1)}`;
}

function SubscriptionRow({
  sub,
  onChanged,
}: {
  sub: ClientSubscriptionBrief;
  onChanged: () => void;
}) {
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const { mutate: toggleFreeze, isPending: freezing } = useMutation({
    mutationFn: () =>
      sub.is_frozen ? api.unfreezeSubscription(sub.id) : api.freezeSubscription(sub.id),
    onSuccess: () => {
      haptic('success');
      toast.success(sub.is_frozen ? 'Разморожено' : 'Заморожено');
      onChanged();
    },
    onError: (e) => {
      haptic('error');
      // Пробную подписку заморозить нельзя: AdaptGroup её не морозит, счётчик дней
      // продолжает идти. Бэк отдаёт 409, объясняем по-человечески.
      if (apiErrorCode(e) === 'app.subscriptions.trial_cannot_freeze') {
        toast.error('Пробную подписку заморозить нельзя');
        return;
      }
      toast.error('Не удалось');
    },
  });

  const { data: devices, isFetching: devicesLoading } = useQuery({
    queryKey: ['sub-devices', sub.id],
    queryFn: () => api.subscriptionDevices(sub.id),
    enabled: devicesOpen,
  });

  const { data: traffic } = useQuery({
    queryKey: ['sub-traffic', sub.id],
    queryFn: () => api.subscriptionTraffic(sub.id),
    enabled: devicesOpen,
    staleTime: 60_000,
  });

  return (
    <div className="py-2 border-b border-white/5 last:border-0">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{sub.plan_name}</div>
          <div className="text-[10px] text-muted-foreground">
            {sub.bot_username ? `@${sub.bot_username}` : '—'} ·{' '}
            {new Date(sub.end_date).toLocaleDateString('ru')}
          </div>
          <CopyValue
            value={sub.adaptgroup_uuid}
            title={sub.supplier === 'scvpn' ? 'пользователь FranCenter' : 'UUID AdaptGroup'}
            className="text-[10px] text-muted-foreground"
          />
          {sub.subscription_url && (
            <CopyValue
              value={sub.subscription_url}
              label="ссылка подписки"
              title="ссылка подписки"
              className="text-[10px] text-muted-foreground"
            />
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {sub.is_frozen ? (
            <span className="text-[10px] font-bold bg-sky-500/15 text-sky-400 px-2 py-0.5 rounded-full">
              FROZEN
            </span>
          ) : sub.is_active ? (
            <span className="text-[10px] font-bold bg-green-500/15 text-green-500 px-2 py-0.5 rounded-full">
              ACTIVE
            </span>
          ) : (
            <span className="text-[10px] font-bold bg-white/10 text-muted-foreground px-2 py-0.5 rounded-full">
              EXPIRED
            </span>
          )}
        </div>
      </div>
      {sub.is_active && (
        <div className="flex items-center gap-2 mt-2">
          <button
            type="button"
            disabled={freezing}
            onClick={() => toggleFreeze()}
            className={`flex-1 py-1.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50 ${
              sub.is_frozen
                ? 'bg-amber-500/15 text-amber-500'
                : 'bg-sky-500/15 text-sky-400'
            }`}
          >
            {freezing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : sub.is_frozen ? (
              <Sun className="w-3.5 h-3.5" />
            ) : (
              <Snowflake className="w-3.5 h-3.5" />
            )}
            {sub.is_frozen ? 'Разморозить' : 'Заморозить'}
          </button>
          <button
            type="button"
            onClick={() => setDevicesOpen((v) => !v)}
            className="flex-1 py-1.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 bg-white/5 text-foreground"
          >
            <Smartphone className="w-3.5 h-3.5" />
            Устройства ({sub.devices})
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${devicesOpen ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
      )}
      <Link
        to="/subscriptions/$id"
        params={{ id: sub.id }}
        className="w-full mt-2 py-1.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 bg-primary/15 text-primary"
      >
        <Stethoscope className="w-3.5 h-3.5" />
        Не подключается: карточка подписки
      </Link>
      {sub.is_active && (
        <button
          type="button"
          onClick={() => setTransferOpen(true)}
          className="w-full mt-2 py-1.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 bg-white/5 text-foreground"
        >
          <ArrowLeftRight className="w-3.5 h-3.5" />
          Перенести на другой аккаунт
        </button>
      )}
      <TransferSubscriptionSheet
        subId={sub.id}
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
      />
      {devicesOpen && (
        <div className="mt-2 space-y-1">
          <div className="flex items-center justify-between text-[11px] bg-white/5 rounded-lg px-3 py-1.5">
            <span className="text-muted-foreground">Трафик (белые списки)</span>
            <span className="font-semibold">{formatTraffic(traffic)}</span>
          </div>
          {devicesLoading ? (
            <div className="flex justify-center py-2">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
            </div>
          ) : !devices || devices.length === 0 ? (
            <p className="text-[11px] text-muted-foreground py-1">Нет активных устройств</p>
          ) : (
            devices.map((d) => (
              <div
                key={d.id}
                className="flex items-center justify-between text-[11px] bg-white/5 rounded-lg px-3 py-1.5"
              >
                <div className="min-w-0">
                  <div className="font-semibold truncate">{d.name}</div>
                  <div className="text-muted-foreground truncate">
                    {[d.os, d.model, d.ip].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
                {d.last_seen ? (
                  <span className="text-muted-foreground shrink-0 ml-2">
                    {new Date(d.last_seen).toLocaleDateString('ru')}
                  </span>
                ) : d.first_seen ? (
                  <span className="text-muted-foreground shrink-0 ml-2">
                    с {new Date(d.first_seen).toLocaleDateString('ru')}
                  </span>
                ) : null}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Перенос подписки на другой Telegram-аккаунт.
 *
 * Переезжает только подписка: баланс, платежи и история остаются у прежнего клиента.
 * Доступ прежнего владельца по умолчанию отзываем, иначе его VPN-клиент продолжит
 * подключаться по уже импортированному профилю.
 */
function TransferSubscriptionSheet({
  subId,
  open,
  onClose,
}: {
  subId: string;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [tgId, setTgId] = useState('');
  const [username, setUsername] = useState('');
  const [reason, setReason] = useState('');
  const [revoke, setRevoke] = useState(true);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      api.transferSubscription(subId, {
        to_tg_user_id: Number(tgId),
        to_tg_username: username.trim().replace(/^@/, '') || null,
        revoke_devices: revoke,
        reason: reason.trim(),
      }),
    onSuccess: (res) => {
      toast.success(res.to_client_created ? 'Перенесено, клиент создан' : 'Подписка перенесена');
      for (const w of res.warnings) toast.warning(w, { duration: 8000 });
      void qc.invalidateQueries();
      setTgId('');
      setUsername('');
      setReason('');
      onClose();
    },
    onError: (e: Error) => toast.error(e.message || 'Не удалось перенести'),
  });

  const valid = Number(tgId) > 0 && reason.trim().length >= 2;

  return (
    <BottomSheet open={open} onClose={onClose} title="Перенести подписку">
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Переезжает только подписка. Баланс, платежи и история останутся у прежнего
          владельца: их переносим отдельно, если понадобится.
        </p>
        <input
          type="text"
          inputMode="numeric"
          value={tgId}
          onChange={(e) => setTgId(e.target.value.replace(/[^0-9]/g, ''))}
          placeholder="Telegram ID получателя"
          className="w-full bg-white/5 rounded-xl px-3.5 py-2.5 text-sm outline-none"
        />
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="@ник получателя (необязательно)"
          className="w-full bg-white/5 rounded-xl px-3.5 py-2.5 text-sm outline-none"
        />
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Причина (уйдёт в аудит)"
          className="w-full bg-white/5 rounded-xl px-3.5 py-2.5 text-sm outline-none"
        />
        <label className="flex items-center justify-between gap-3 text-sm">
          <span>Отозвать доступ прежнего владельца</span>
          <input
            type="checkbox"
            checked={revoke}
            onChange={(e) => setRevoke(e.target.checked)}
            className="w-5 h-5 accent-primary"
          />
        </label>
        {revoke && (
          <p className="text-[11px] text-amber-500 leading-snug">
            Ссылка на подписку перевыпустится, устройства снимутся. Уже импортированный
            профиль может работать до смены серверов.
          </p>
        )}
        <button
          type="button"
          disabled={!valid || isPending}
          onClick={() => mutate()}
          className="w-full bg-primary text-primary-foreground py-2.5 rounded-xl text-sm font-bold disabled:opacity-50"
        >
          {isPending ? 'Переношу…' : 'Перенести'}
        </button>
      </div>
    </BottomSheet>
  );
}


function TopupSheet({
  open,
  onClose,
  clientId,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  onSuccess: () => void;
}) {
  // Режим: начислить (+) или списать (−). Оба идут через adjust (signed amount,
  // с reason + audit на бэке). Списание не уводит баланс в минус — бэк проверяет.
  const [mode, setMode] = useState<'add' | 'subtract'>('add');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      const cents = Math.round(parseFloat(amount) * 100);
      if (Number.isNaN(cents) || cents <= 0) {
        throw new Error('Сумма > 0');
      }
      const text = reason.trim() || (mode === 'add' ? 'начисление' : 'списание');
      return api.adjustClientBalance(clientId, {
        amount_minor: mode === 'add' ? cents : -cents,
        reason: text.slice(0, 200),
      });
    },
    onSuccess: () => {
      haptic('success');
      toast.success(mode === 'add' ? 'Начислено' : 'Списано');
      setAmount('');
      setReason('');
      onSuccess();
      onClose();
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message || 'Не сохранилось');
    },
  });

  const add = mode === 'add';
  return (
    <BottomSheet open={open} onClose={onClose} title="Изменить баланс клиента">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setMode('add')}
            className={`py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 transition-colors ${
              add ? 'bg-green-500/15 text-green-500' : 'bg-white/5 text-muted-foreground'
            }`}
          >
            <Plus className="w-4 h-4" /> Начислить
          </button>
          <button
            type="button"
            onClick={() => setMode('subtract')}
            className={`py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 transition-colors ${
              !add ? 'bg-red-500/15 text-red-500' : 'bg-white/5 text-muted-foreground'
            }`}
          >
            <Minus className="w-4 h-4" /> Списать
          </button>
        </div>
        <Field label="Сумма (USD)">
          <input
            type="number"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="10.00"
            className="w-full bg-white/5 border border-white/5 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </Field>
        <Field label="Причина (для audit)">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={add ? 'компенсация / бонус' : 'корректировка / возврат'}
            className="w-full bg-white/5 border border-white/5 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </Field>
      </div>
      <button
        type="button"
        onClick={() => mutate()}
        disabled={isPending || !amount}
        className={`w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 ${
          add ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}
      >
        {isPending ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : add ? (
          <Plus className="w-4 h-4" />
        ) : (
          <Minus className="w-4 h-4" />
        )}
        {add ? 'Начислить' : 'Списать'}
      </button>
    </BottomSheet>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-semibold">{label}</label>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono text-xs">{value}</span>
    </div>
  );
}
